import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, Trash2, Loader2, FileImage } from "lucide-react";

/** Client-side resize to keep base64 lean before upload (max 1024px, JPEG q=0.85). */
async function resizeToJpegBase64(file, maxDim = 1024, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(maxDim / img.width, maxDim / img.height, 1);
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** Single KYC document upload slot.
 *  Props: { kind, label, hint, currentDataUri, onUploaded, onDeleted, locked }
 *  - kind: "pan" | "aadhaar_front" | "aadhaar_back" | "bank"
 *  - locked: when true, no upload/replace/delete buttons (e.g. profile Approved)
 */
export function KycDocUploader({ kind, label, hint, currentDataUri, onUploaded, onDeleted, locked }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(false);
  const pick = () => fileRef.current?.click();

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error("JPG / PNG / WebP only");
      return;
    }
    setBusy(true);
    try {
      const dataUri = await resizeToJpegBase64(file, 1280, 0.85);
      await api.post("/profile/kyc-doc", { kind, image_base64: dataUri });
      toast.success(`${label} uploaded`);
      onUploaded?.(dataUri);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!currentDataUri) return;
    if (!window.confirm(`Remove your ${label.toLowerCase()}?`)) return;
    try {
      await api.delete(`/profile/kyc-doc/${kind}`);
      toast.success(`${label} removed`);
      onDeleted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="border border-stone-200 rounded-xl p-3 bg-stone-50/60" data-testid={`kyc-doc-${kind}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <div className="font-semibold text-sm" data-testid={`kyc-doc-${kind}-label`}>{label}</div>
          {hint && <div className="text-[11px] text-stone-500">{hint}</div>}
        </div>
        {currentDataUri && (
          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid={`kyc-doc-${kind}-status`}>
            Uploaded
          </span>
        )}
      </div>

      <div className="relative aspect-[16/10] bg-white border border-dashed border-stone-300 rounded-lg overflow-hidden grid place-items-center">
        {currentDataUri ? (
          <img src={currentDataUri} alt={label} onClick={() => setZoom(true)}
            className="w-full h-full object-contain cursor-zoom-in"
            data-testid={`kyc-doc-${kind}-preview`} />
        ) : (
          <div className="flex flex-col items-center text-stone-400 px-3 text-center">
            <FileImage className="w-7 h-7 mb-1" />
            <span className="text-xs">No file yet</span>
          </div>
        )}
        {busy && <div className="absolute inset-0 bg-white/70 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-900" /></div>}
      </div>

      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPick} data-testid={`kyc-doc-${kind}-input`} />
      {!locked && (
        <div className="flex gap-2 mt-2">
          <Button type="button" onClick={pick} disabled={busy}
            data-testid={`kyc-doc-${kind}-upload-btn`}
            className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold h-8 px-3 text-xs flex-1">
            <Upload className="w-3.5 h-3.5 mr-1.5" /> {currentDataUri ? "Replace" : "Upload"}
          </Button>
          {currentDataUri && (
            <Button type="button" variant="outline" onClick={remove} disabled={busy}
              data-testid={`kyc-doc-${kind}-remove-btn`}
              className="rounded-full border-stone-300 text-red-700 hover:bg-red-50 h-8 px-3 text-xs">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      )}

      {zoom && currentDataUri && (
        <div className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-6" onClick={() => setZoom(false)} data-testid={`kyc-doc-${kind}-zoom`}>
          <img src={currentDataUri} alt={label} className="max-w-full max-h-full rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
}

/** Combined section: 4 KYC document slots in a grid + onChange propagation. */
export default function KycDocsSection({ form, onChange, locked }) {
  const update = (kind, value) => onChange?.({
    ...form,
    [{
      pan: "pan_doc_base64",
      aadhaar_front: "aadhaar_front_base64",
      aadhaar_back: "aadhaar_back_base64",
      bank: "bank_doc_base64",
    }[kind]]: value,
  });

  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="kyc-docs-grid">
      <KycDocUploader kind="pan" label="PAN card (front)" hint="Clear photo of your PAN card."
        currentDataUri={form.pan_doc_base64} locked={locked}
        onUploaded={(d) => update("pan", d)} onDeleted={() => update("pan", null)} />
      <KycDocUploader kind="aadhaar_front" label="Aadhaar (front)" hint="Side with your photo + Aadhaar number."
        currentDataUri={form.aadhaar_front_base64} locked={locked}
        onUploaded={(d) => update("aadhaar_front", d)} onDeleted={() => update("aadhaar_front", null)} />
      <KycDocUploader kind="aadhaar_back" label="Aadhaar (back)" hint="Address side."
        currentDataUri={form.aadhaar_back_base64} locked={locked}
        onUploaded={(d) => update("aadhaar_back", d)} onDeleted={() => update("aadhaar_back", null)} />
      <KycDocUploader kind="bank" label="Cancelled cheque / passbook" hint="Showing account number, IFSC, holder name."
        currentDataUri={form.bank_doc_base64} locked={locked}
        onUploaded={(d) => update("bank", d)} onDeleted={() => update("bank", null)} />
    </div>
  );
}
